import { apiGet } from './api';
import { 
  ComplaintReport, 
  UserReport, 
  DepartmentReport, 
  EventReport,
  BusinessReport,
  ChargeReport,
  ElectionReport,
  TimeRangeReport,
  BlockWiseReport,
  CategoryWiseReport,
  PriorityWiseReport,
  StatusWiseReport,
  DashboardReport,
  ReportFilters
} from '@/types';

class ReportService {

  /**
   * Get complaint reports
   */
  async getComplaintReport(filters: ReportFilters = {}): Promise<ComplaintReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await apiGet<{ success: boolean; data: ComplaintReport }>(
        `/reports/complaints?${params.toString()}`
      );
      return (response as any).data || response;
    } catch (error) {
      console.error('Error fetching complaint report:', error);
      throw error;
    }
  }

  /**
   * Get user reports
   */
  async getUserReport(filters: ReportFilters = {}): Promise<UserReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/users?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user report:', error);
      throw error;
    }
  }

  /**
   * Get department reports
   */
  async getDepartmentReport(filters: ReportFilters = {}): Promise<DepartmentReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/departments?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching department report:', error);
      throw error;
    }
  }

  /**
   * Get event reports
   */
  async getEventReport(filters: ReportFilters = {}): Promise<EventReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/events?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching event report:', error);
      throw error;
    }
  }

  /**
   * Get business reports
   */
  async getBusinessReport(filters: ReportFilters = {}): Promise<BusinessReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/businesses?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching business report:', error);
      throw error;
    }
  }

  /**
   * Get charge reports
   */
  async getChargeReport(filters: ReportFilters = {}): Promise<ChargeReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/charges?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching charge report:', error);
      throw error;
    }
  }

  /**
   * Get election reports
   */
  async getElectionReport(filters: ReportFilters = {}): Promise<ElectionReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/elections?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching election report:', error);
      throw error;
    }
  }

  /**
   * Get time range reports
   */
  async getTimeRangeReport(
    type: 'weekly' | 'monthly' | 'quarterly',
    filters: ReportFilters = {}
  ): Promise<TimeRangeReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/time-range/${type}?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching time range report:', error);
      throw error;
    }
  }

  /**
   * Get block-wise reports
   */
  async getBlockWiseReport(filters: ReportFilters = {}): Promise<BlockWiseReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/block-wise?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching block-wise report:', error);
      throw error;
    }
  }

  /**
   * Get category-wise reports
   */
  async getCategoryWiseReport(filters: ReportFilters = {}): Promise<CategoryWiseReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/category-wise?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching category-wise report:', error);
      throw error;
    }
  }

  /**
   * Get priority-wise reports
   */
  async getPriorityWiseReport(filters: ReportFilters = {}): Promise<PriorityWiseReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/priority-wise?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching priority-wise report:', error);
      throw error;
    }
  }

  /**
   * Get status-wise reports
   */
  async getStatusWiseReport(filters: ReportFilters = {}): Promise<StatusWiseReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/status-wise?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching status-wise report:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive dashboard report
   */
  async getDashboardReport(filters: ReportFilters = {}): Promise<DashboardReport> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await apiGet<{ success: boolean; data: DashboardReport }>(
        `/reports/dashboard?${params.toString()}`
      );
      return (response as any).data || response;
    } catch (error) {
      console.error('Error fetching dashboard report:', error);
      throw error;
    }
  }

  /**
   * Get complaints without updates within 24 hours
   */
  async getComplaintsWithoutUpdates(): Promise<any> {
    try {
      const response = await apiGet<{ success: boolean; data: any }>(
        '/reports/complaints/no-updates'
      );
      return (response as any).data || response;
    } catch (error) {
      console.error('Error fetching complaints without updates:', error);
      throw error;
    }
  }

  /**
   * Get escalated complaints
   */
  async getEscalatedComplaints(): Promise<any> {
    try {
      const response = await apiGet<{ success: boolean; data: any; pagination?: any }>(
        '/complaints/escalated'
      );
      // Handle both response formats
      if ((response as any).data) {
        return response;
      }
      return { data: response, pagination: { total: Array.isArray(response) ? response.length : 0 } };
    } catch (error) {
      console.error('Error fetching escalated complaints:', error);
      // Return empty data instead of throwing to prevent breaking the page
      return { data: [], pagination: { total: 0 } };
    }
  }

  /**
   * Export report data
   */
  async exportReport(
    type: string,
    format: 'json' | 'csv' | 'xlsx',
    filters: ReportFilters = {}
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(
        `${this.baseURL}/export/${type}/${format}?${params.toString()}`,
        { 
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  /**
   * Download report file
   */
  async downloadReport(
    type: string,
    format: 'json' | 'csv' | 'xlsx',
    filters: ReportFilters = {}
  ): Promise<void> {
    try {
      const blob = await this.exportReport(type, format, filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      throw error;
    }
  }

  /**
   * Get date range for different periods
   */
  getDateRange(period: 'week' | 'month' | 'quarter' | 'year'): { from: string; to: string } {
    const now = new Date();
    const to = now.toISOString().split('T')[0];
    let from: string;

    switch (period) {
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        from = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
        from = monthAgo.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
        from = quarterStart.toISOString().split('T')[0];
        break;
      case 'year':
        const yearStart = new Date(now.getFullYear(), 0, 1);
        from = yearStart.toISOString().split('T')[0];
        break;
      default:
        from = now.toISOString().split('T')[0];
    }

    return { from, to };
  }

  /**
   * Format date for display
   */
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }
}

export default new ReportService();
